import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  Close as CloseIcon,
  FileCopy as FileCopyIcon
} from '@material-ui/icons';

import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { FileDto } from '../Document.interface';
import AssignDocumentDialog from './AssignDocumentDialog';

const StyledTableCell = styled(TableCell)`
  .MuiButtonBase-root {
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
`;

export interface DocumentReferenceCardProps {
  files: FileDto[];
  title?: string;
  assignLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
  orderDirection?: 'ASC' | 'DESC';
  actions?: React.ReactNode;
  isActive?: (order: string) => boolean;
  setOrder?: (order?: string) => void;
  setOrderDirection?: (orderDirection?: 'ASC' | 'DESC') => void;
  onRemove?: (id: number) => void;
  onAssigned?: (files: FileDto[]) => void;
}

const DocumentReferenceCard: React.FC<DocumentReferenceCardProps> = (
  props: DocumentReferenceCardProps
) => {
  const {
    files,
    actions,
    disabled = false,
    isLoading = false,
    title = 'Document References',
    assignLabel = 'Assign Document',
    orderDirection,
    isActive,
    setOrder,
    setOrderDirection,
    onRemove,
    onAssigned
  } = props;
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [removeFile, setRemoveFile] = useState<FileDto>();

  const handleSort = (column: string) => {
    setOrder?.(column);
    setOrderDirection?.(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive?.(column) && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <Card>
      <CardTitle>
        <FileCopyIcon />
        {title}
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={isActive?.('files.id')}
                  direction={getSortDirection('files.id')}
                  onClick={() => handleSort('files.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={isActive?.('files.name')}
                  direction={getSortDirection('files.name')}
                  onClick={() => handleSort('files.name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={isActive?.('files.version')}
                  direction={getSortDirection('files.version')}
                  onClick={() => handleSort('files.version')}
                >
                  Version
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={isActive?.('task.status')}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {files?.map((file: FileDto) => (
              <DocumentReferenceCardItem
                key={file.id}
                file={file}
                disabled={disabled}
                onRemove={() => setRemoveFile(file)}
              />
            ))}
            <TableRow>
              <StyledTableCell colSpan={5}>
                <Button
                  color='primary'
                  variant='outlined'
                  disabled={disabled}
                  onClick={() => setOpenDialog(true)}
                >
                  {assignLabel}
                </Button>
                {actions}
              </StyledTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog && (
        <AssignDocumentDialog
          open={openDialog}
          selectedItems={files}
          onCancel={() => setOpenDialog(false)}
          onSubmit={(files: FileDto[]) => {
            onAssigned?.(files);
            setOpenDialog(false);
          }}
        />
      )}
      <ConfirmDialog
        title='Unlink Document'
        open={!!removeFile}
        message={`Are you sure you want to unlink ${
          removeFile?.task?.key || `DOC-${removeFile?.id || '?'}`
        }?`}
        onCancel={() => setRemoveFile(undefined)}
        onConfirm={() => {
          setRemoveFile(undefined);
          if (removeFile) {
            onRemove?.(removeFile.id);
          }
        }}
      />
    </Card>
  );
};

export default DocumentReferenceCard;

export interface ReferenceCardItemProps {
  file: FileDto;
  disabled?: boolean;
  onRemove?: () => void;
}

export const DocumentReferenceCardItem: React.FC<ReferenceCardItemProps> = (
  props: ReferenceCardItemProps
) => {
  const { file, disabled = false, onRemove } = props;
  const history = useHistory();
  const versionLog = file.task?.versionLog;
  const version =
    versionLog?.version || file.task?.version || file.version || 1;
  return (
    <TableRow
      hover
      onClick={() => {
        // set version params if navigating previous version
        const params = !versionLog?.isLatest ? `?ver=${version}` : '';
        const redirectPath = `/portal/documents/file/task/${file.taskId}${params}`;
        history.push(redirectPath);
      }}
    >
      <TableCell>{file.task?.key || `DOC-${file.id}`}</TableCell>
      <WrappedTableCell>{file.name}</WrappedTableCell>
      <TableCell>{version}</TableCell>
      <TableCell>
        <TaskStatusLabel
          // use fileVersion.task if exist for displaying
          // previous file version status
          task={file.fileVersion ? file.fileVersion.task : file.task}
        />
      </TableCell>
      <TableCell padding='checkbox'>
        <IconButton
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onRemove?.();
          }}
        >
          <CloseIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
